import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useUserData } from '../contexts/UserDataContext';

const {
  FiCheckCircle, FiCircle, FiClock, FiCalendar, FiPlus, FiEdit3,
  FiX, FiSave, FiTrash2, FiTarget, FiTrendingUp, FiAward,
  FiLayers, FiChevronDown, FiChevronUp, FiInfo, FiZap
} = FiIcons;

// Milestone Templates organized by phase
const MILESTONE_TEMPLATES = {
  'Phase 1: Foundation & Exploration (Years 1-2)': [
    {
      title: 'Academic Baseline Established',
      description: 'Create core-course tracker (NCAA D1/D2), plan course rigor (Honors/AP/IB) aligned to target admissions, and set test plan (PSAT + SAT/ACT prep timeline).',
      category: 'academic',
      suggestedYear: 1
    },
    {
      title: 'Athletic Profile Created',
      description: 'Add grad year, HS + club team, coach contact info, GPA scale, class rank, schedule, verified measurables, and academic interests/major.',
      category: 'profile',
      suggestedYear: 1
    },
    {
      title: 'Target School List Initialized',
      description: 'Create first draft target list across all divisions (D1, D2, D3, NAIA) focusing on academic and athletic fit.',
      category: 'recruitment',
      suggestedYear: 1
    },
    {
      title: 'NCAA Free Profile Page Created',
      description: 'Create a free Profile Page account with the NCAA Eligibility Center to get your NCAA ID number early in your high school career.',
      category: 'admin',
      suggestedYear: 2
    },
    {
      title: 'First Highlight Video & Assets Uploaded',
      description: 'Must include: intro slate (name/grad year/contact), best plays first. Plus, add separate 30-60s skills/intro clip and 2-3 full-game films (unlisted links).',
      category: 'content',
      suggestedYear: 1
    },
    {
      title: 'Skills-Specific Video Captured',
      description: 'Beyond game highlights, coaches want to see raw mechanics (e.g., a pitcher\'s bullpens, a QB\'s footwork, or a hitter\'s swing).',
      category: 'content',
      suggestedYear: 1
    },
    {
      title: 'Outreach System Built',
      description: 'Create templates (intro, schedule/film/measurables updates, post-event follow-up) and submit team recruiting questionnaires for initial targets.',
      category: 'communication',
      suggestedYear: 2
    },
    {
      title: 'Recruiting CRM Initialized',
      description: 'Set up CRM/spreadsheet tracking: School, Division, Coaches/emails, Questionnaire status, Outreach dates, Responses, Calls/Visits, Film sent, Academic/Aid notes, Next action date.',
      category: 'admin',
      suggestedYear: 1
    },
    {
      title: 'Camp & Showcase Strategy',
      description: 'Identify and plan which summer camps or showcases provide the best exposure for your specific target school list.',
      category: 'recruitment',
      suggestedYear: 1
    },
    {
      title: 'References Prepared',
      description: 'Confirm HS coach and club coach agree to be contacts; add their contact details to your profile and resume.',
      category: 'profile',
      suggestedYear: 1
    }
  ],
  'Phase 2: Active Recruitment & Showcasing (Year 3)': [
    {
      title: 'Eligibility Center Registration & Workflow',
      description: 'Register with NCAA (and NAIA if applicable). Request official transcript submission, enter coursework, update amateurism info, and track D1/D2 vs D3/NAIA requirements.',
      category: 'admin',
      suggestedYear: 3
    },
    {
      title: 'The June 15th / Sept 1st Go-Live Dates',
      description: 'Depending on the sport, these are the dates when D1/D2 coaches can initiate direct contact. Ensure your profile, film, and contact info are ready.',
      category: 'communication',
      suggestedYear: 3
    },
    {
      title: 'Target School Questionnaires Completed',
      description: 'Fill out specific recruiting questionnaires on the athletics websites of all your target schools to ensure you enter their recruiting databases.',
      category: 'admin',
      suggestedYear: 3
    },
    {
      title: 'Social Media Audit Completed',
      description: 'Review and clean up all social media profiles (Instagram, Twitter/X, TikTok) for coach visibility.',
      category: 'profile',
      suggestedYear: 3
    },
    {
      title: 'Unofficial Campus Visit Completed',
      description: 'Required: meeting plan (coach, facilities, academic advisor/admissions if possible), and send post-visit follow-up email within 24-48 hours.',
      category: 'recruitment',
      suggestedYear: 3
    },
    {
      title: 'Junior Season Highlight Reel & Film',
      description: 'Publish junior-season highlights, a refreshed skills clip, and 2-3 junior full games (unlisted links).',
      category: 'content',
      suggestedYear: 3
    },
    {
      title: 'Recruiting Traction Achieved',
      description: 'Measurable outputs: ≥15 target coaches contacted, ≥5 replies, ≥2 calls/Zooms, ≥1 evaluation next step (camp invite, visit request, film request).',
      category: 'communication',
      suggestedYear: 3
    },
    {
      title: 'Coach Call Prep & Mock Interviews',
      description: 'Prepare a "Cheat Sheet" of questions to ask and practice mock interviews for when college coaches call.',
      category: 'communication',
      suggestedYear: 3
    },
    {
      title: 'Standardized Test Score or Plan Confirmed',
      description: 'Achieve target test score OR confirm test-optional plan per school. Send official scores where required.',
      category: 'academic',
      suggestedYear: 3
    },
    {
      title: 'Camps/Showcases Executed',
      description: 'Attend a defined set of events (2-6). Notify coaches beforehand, log attendance, track results/metrics, and send follow-ups.',
      category: 'recruitment',
      suggestedYear: 3
    },
    {
      title: 'Net Price & Aid Stacking Worksheet',
      description: 'Complete Net Price Calculator (NPC) results + estimate likely merit/need-based + athletic aid for each serious target school.',
      category: 'admin',
      suggestedYear: 3
    },
    {
      title: 'Coach Call/Zoom Cycle Established',
      description: 'Establish and document a recurring communication cadence with interested coaches in your CRM.',
      category: 'communication',
      suggestedYear: 3
    }
  ],
  'Phase 3: The Decision & Execution (Year 4)': [
    {
      title: 'Top 10 Schools List Defined',
      description: 'Narrow target list to focused "Top 10" schools with balance of Reach, Target, and Safety schools.',
      category: 'recruitment',
      suggestedYear: 4
    },
    {
      title: 'Official Visits Completed',
      description: 'Confirm required documents submitted (NCAA ID, transcripts, test scores, admissions preread), then complete official paid visits.',
      category: 'recruitment',
      suggestedYear: 4
    },
    {
      title: 'Offer Packet Evaluated',
      description: 'Capture written details of offers: amount, duration, renewal conditions, roster role, medical/injury terms, and Cost of Attendance/net cost after all aid.',
      category: 'recruitment',
      suggestedYear: 4
    },
    {
      title: 'Verbal Commitment',
      description: 'Make your verbal commitment to the coaching staff and publicize it (this gap usually occurs between receiving the offer and signing the NLI).',
      category: 'communication',
      suggestedYear: 4
    },
    {
      title: 'Commitment Executed',
      description: 'Execute commitment via correct mechanism: National Letter of Intent (NLI), written athletic aid agreement/tender, or admissions commit (D3/Ivy-style).',
      category: 'admin',
      suggestedYear: 4
    },
    {
      title: 'Financial Aid Award Letter Review',
      description: 'Compare the athletic scholarship offer with the total cost of attendance (COA) and other aid based on your FAFSA and CSS Profile results.',
      category: 'admin',
      suggestedYear: 4
    },
    {
      title: 'University Admissions Application',
      description: 'Submit formal applications to the university. Getting an athletic scholarship offer doesn\'t mean you are automatically admitted to the university.',
      category: 'academic',
      suggestedYear: 4
    },
    {
      title: 'Backup Plan Maintained',
      description: 'Continue outreach to alternate schools until your commitment is binding/confirmed and admissions is fully secure.',
      category: 'recruitment',
      suggestedYear: 4
    }
  ],
  'Phase 4: Ongoing & Administrative': [
    {
      title: 'Continuous CRM Logging',
      description: 'Maintain continuous CRM logging with outcome fields: response status, evaluation status, next action date, and last contact date.',
      category: 'communication',
      suggestedYear: 0
    },
    {
      title: 'Final Transcripts & Proof of Graduation Sent',
      description: 'Send final high school transcripts and proof of graduation to university admissions and NCAA Eligibility Center as needed.',
      category: 'academic',
      suggestedYear: 4
    },
    {
      title: 'Eligibility Center Final Certification',
      description: 'For D1/D2: Ensure final transcript is processed and final amateurism/academic status is officially cleared.',
      category: 'admin',
      suggestedYear: 4
    },
    {
      title: 'Medical & Compliance Onboarding Completed',
      description: 'Complete sports physical, immunizations, insurance, required athletics forms, and any assigned compliance modules.',
      category: 'admin',
      suggestedYear: 4
    },
    {
      title: 'Housing & Orientation Administrative Tasks',
      description: 'Complete all administrative tasks for your transition to campus life, including housing applications, deposits, and orientation sign-ups.',
      category: 'admin',
      suggestedYear: 4
    },
    {
      title: 'Summer Workout Program',
      description: 'Receive and begin the mandatory strength and conditioning program sent by your new college coaches prior to your arrival.',
      category: 'profile',
      suggestedYear: 4
    },
    {
      title: 'The Handoff - Profile Transferred',
      description: 'Archive journey data into "Welcome to College Athletics" package and transition to college compliance/onboarding resources.',
      category: 'admin',
      suggestedYear: 4
    }
  ]
};

const getCategoryColor = (category) => {
  const colors = {
    academic: 'bg-purple-100 text-purple-700',
    profile: 'bg-blue-100 text-blue-700',
    recruitment: 'bg-green-100 text-green-700',
    content: 'bg-orange-100 text-orange-700',
    communication: 'bg-pink-100 text-pink-700',
    admin: 'bg-gray-100 text-gray-700'
  };
  return colors[category] || 'bg-gray-100 text-gray-700';
};

const getCategoryIcon = (category) => {
  const icons = {
    academic: FiTarget,
    profile: FiEdit3,
    recruitment: FiTrendingUp,
    content: FiAward,
    communication: FiClock,
    admin: FiLayers
  };
  return icons[category] || FiCircle;
};

function TimelineTracker() {
  const { getUserData, updateUserData } = useUserData();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [expandedPhases, setExpandedPhases] = useState({});
  const [milestoneForm, setMilestoneForm] = useState({
    title: '',
    description: '',
    dueDate: '',
    notes: '',
    category: ''
  });

  const [milestones, setMilestones] = useState([]);

  useEffect(() => {
    const userMilestones = getUserData('milestones') || [];
    setMilestones(userMilestones);
  }, []);

  useEffect(() => {
    updateUserData('milestones', milestones);
  }, [milestones]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-500" />;
      case 'in-progress': return <SafeIcon icon={FiClock} className="w-6 h-6 text-yellow-500" />;
      default: return <SafeIcon icon={FiCircle} className="w-6 h-6 text-gray-300" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in-progress': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const handleAddCustomMilestone = () => {
    setEditingMilestone(null);
    setMilestoneForm({
      title: '',
      description: '',
      dueDate: '',
      notes: '',
      category: ''
    });
    setShowAddModal(true);
  };

  const handleShowTemplates = () => {
    setShowTemplatesModal(true);
  };

  const handleAddTemplatedMilestone = (template, phaseName) => {
    const today = new Date();
    const suggestedDate = new Date(today.setMonth(today.getMonth() + 1));
    
    const newMilestone = {
      id: Date.now(),
      title: template.title,
      description: template.description,
      dueDate: suggestedDate.toISOString().split('T')[0],
      status: 'pending',
      category: template.category,
      phase: phaseName,
      isTemplate: true
    };
    
    setMilestones([...milestones, newMilestone]);
  };

  const handleAddAllPhase = (phaseName) => {
    const templates = MILESTONE_TEMPLATES[phaseName];
    const today = new Date();
    
    const newMilestones = templates.map((template, index) => {
      const dueDate = new Date(today);
      dueDate.setMonth(dueDate.getMonth() + index + 1);
      
      return {
        id: Date.now() + index,
        title: template.title,
        description: template.description,
        dueDate: dueDate.toISOString().split('T')[0],
        status: 'pending',
        category: template.category,
        phase: phaseName,
        isTemplate: true
      };
    });
    
    setMilestones([...milestones, ...newMilestones]);
  };

  const handleEditMilestone = (milestone) => {
    setEditingMilestone(milestone.id);
    setMilestoneForm({
      title: milestone.title,
      description: milestone.description,
      dueDate: milestone.dueDate,
      notes: milestone.notes || '',
      category: milestone.category || ''
    });
    setShowAddModal(true);
  };

  const handleSaveMilestone = () => {
    if (!milestoneForm.title || !milestoneForm.dueDate) {
      alert('Please fill in at least the title and due date');
      return;
    }

    if (editingMilestone) {
      setMilestones(
        milestones.map((milestone) =>
          milestone.id === editingMilestone
            ? { ...milestone, ...milestoneForm }
            : milestone
        )
      );
    } else {
      const newMilestone = {
        ...milestoneForm,
        id: Date.now(),
        status: 'pending',
        isTemplate: false
      };
      setMilestones([...milestones, newMilestone]);
    }

    setShowAddModal(false);
    setEditingMilestone(null);
  };

  const handleDeleteMilestone = (milestoneId) => {
    if (window.confirm('Are you sure you want to delete this milestone?')) {
      setMilestones(milestones.filter((milestone) => milestone.id !== milestoneId));
    }
  };

  const handleStatusChange = (milestoneId, newStatus) => {
    setMilestones(
      milestones.map((milestone) => {
        if (milestone.id === milestoneId) {
          const updates = { status: newStatus };
          if (newStatus === 'completed' && !milestone.completedDate) {
            updates.completedDate = new Date().toISOString().split('T')[0];
          } else if (newStatus !== 'completed') {
            updates.completedDate = null;
          }
          return { ...milestone, ...updates };
        }
        return milestone;
      })
    );
  };

  const handleProgressChange = (milestoneId, progress) => {
    setMilestones(
      milestones.map((milestone) =>
        milestone.id === milestoneId
          ? { ...milestone, progress: parseInt(progress) }
          : milestone
      )
    );
  };

  const togglePhase = (phaseName) => {
    setExpandedPhases(prev => ({
      ...prev,
      [phaseName]: !prev[phaseName]
    }));
  };

  const getPhaseProgress = (phaseName) => {
    const phaseTemplates = MILESTONE_TEMPLATES[phaseName];
    const phaseMilestones = milestones.filter(m => m.phase === phaseName);
    const completed = phaseMilestones.filter(m => m.status === 'completed').length;
    const total = phaseTemplates.length;
    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  };

  const completedCount = milestones.filter((m) => m.status === 'completed').length;
  const totalCount = milestones.length;
  const overallProgress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Progress Overview */}
      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h2 className="text-lg sm:text-xl font-space-grotesk font-semibold text-secondary">
            Scholarship Timeline Progress
          </h2>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <button
              onClick={handleShowTemplates}
              className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all btn-primary flex items-center justify-center space-x-2 shadow-md text-sm"
            >
              <SafeIcon icon={FiZap} className="w-4 h-4" />
              <span>Quick Start Templates</span>
            </button>
            <button
              onClick={handleAddCustomMilestone}
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors btn-primary flex items-center justify-center space-x-2 text-sm"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              <span>Add Custom Milestone</span>
            </button>
          </div>
        </div>

        {totalCount > 0 ? (
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Overall Progress</span>
              <span className="text-sm text-gray-500">
                {completedCount} of {totalCount} completed
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${overallProgress}%` }}
                transition={{ duration: 1, delay: 0.5 }}
                className="bg-primary h-2 rounded-full"
              ></motion.div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <SafeIcon icon={FiTarget} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">
              No milestones yet. Get started with our guided templates or create your own!
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <button
                onClick={handleShowTemplates}
                className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-3 rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all btn-primary flex items-center justify-center space-x-2 shadow-md"
              >
                <SafeIcon icon={FiZap} className="w-5 h-5" />
                <span>Use Templates</span>
              </button>
              <button
                onClick={handleAddCustomMilestone}
                className="bg-white border-2 border-primary text-primary px-6 py-3 rounded-lg hover:bg-blue-50 transition-colors flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiPlus} className="w-5 h-5" />
                <span>Create Custom</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Info Box */}
      {totalCount === 0 && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
          <div className="flex items-start space-x-3">
            <SafeIcon icon={FiInfo} className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">
                Guided Journey Templates
              </h4>
              <p className="text-sm text-gray-700 mb-2">
                Our Quick Start Templates provide a proven 4-phase roadmap with essential milestones covering:
              </p>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li><strong>Phase 1:</strong> Foundation & Exploration (Years 1-2)</li>
                <li><strong>Phase 2:</strong> Active Recruitment & Showcasing (Year 3)</li>
                <li><strong>Phase 3:</strong> The Decision & Execution (Year 4)</li>
                <li><strong>Phase 4:</strong> Ongoing & Administrative</li>
              </ul>
              <p className="text-sm text-gray-700 mt-2">
                You can add individual milestones or entire phases, and customize everything to fit your unique journey!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      {totalCount > 0 && (
        <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
          <div className="space-y-6">
            {milestones.map((milestone, index) => (
              <motion.div
                key={milestone.id}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    {getStatusIcon(milestone.status)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-800 break-words">
                            {milestone.title}
                          </h3>
                          {milestone.category && (
                            <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${getCategoryColor(milestone.category)}`}>
                              {milestone.category}
                            </span>
                          )}
                          {milestone.isTemplate && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 whitespace-nowrap">
                              Template
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600 text-sm break-words">{milestone.description}</p>
                      </div>
                      
                      <div className="flex items-center gap-2 sm:ml-4 flex-shrink-0">
                        <select
                          value={milestone.status}
                          onChange={(e) => handleStatusChange(milestone.id, e.target.value)}
                          className={`px-2 py-1 text-xs rounded-full capitalize border-0 focus:ring-2 focus:ring-primary ${getStatusColor(
                            milestone.status
                          )}`}
                        >
                          <option value="pending">Pending</option>
                          <option value="in-progress">In Progress</option>
                          <option value="completed">Completed</option>
                        </select>
                        
                        <button
                          onClick={() => handleEditMilestone(milestone)}
                          className="text-gray-400 hover:text-primary transition-colors"
                        >
                          <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteMilestone(milestone.id)}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-3">
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                        <span>Due: {new Date(milestone.dueDate).toLocaleDateString()}</span>
                      </div>
                      {milestone.completedDate && (
                        <div>
                          <span>
                            Completed: {new Date(milestone.completedDate).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>

                    {milestone.notes && (
                      <div className="bg-blue-50 p-3 rounded-lg mb-3">
                        <p className="text-sm text-blue-800 break-words">{milestone.notes}</p>
                      </div>
                    )}

                    {milestone.status === 'in-progress' && (
                      <div className="mt-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-gray-600">Progress</span>
                          <span className="text-xs text-gray-600">
                            {milestone.progress || 0}%
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={milestone.progress || 0}
                            onChange={(e) => handleProgressChange(milestone.id, e.target.value)}
                            className="flex-1 h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer"
                            style={{
                              background: `linear-gradient(to right, #eab308 0%, #eab308 ${milestone.progress || 0}%, #e5e7eb ${milestone.progress || 0}%, #e5e7eb 100%)`
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {index < milestones.length - 1 && (
                  <div className="absolute left-[11px] top-8 w-px h-12 bg-gray-200"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Templates Modal */}
      <AnimatePresence>
        {showTemplatesModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50"
              onClick={() => setShowTemplatesModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-xl sm:text-2xl font-space-grotesk font-bold text-secondary mb-2">
                        Quick Start Milestone Templates
                      </h2>
                      <p className="text-sm sm:text-base text-gray-600">
                        Choose individual milestones or add entire phases to your timeline
                      </p>
                    </div>
                    <button
                      onClick={() => setShowTemplatesModal(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 ml-4"
                    >
                      <SafeIcon icon={FiX} className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {Object.entries(MILESTONE_TEMPLATES).map(([phaseName, templates]) => {
                      const progress = getPhaseProgress(phaseName);
                      const isExpanded = expandedPhases[phaseName];
                      
                      return (
                        <div key={phaseName} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-3 mb-2">
                                  <button 
                                    onClick={() => togglePhase(phaseName)}
                                    className="flex items-center space-x-2 text-left"
                                  >
                                    <SafeIcon 
                                      icon={isExpanded ? FiChevronUp : FiChevronDown} 
                                      className="w-5 h-5 text-primary flex-shrink-0" 
                                    />
                                    <h3 className="text-base sm:text-lg font-space-grotesk font-semibold text-secondary break-words">
                                      {phaseName}
                                    </h3>
                                  </button>
                                  <span className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                                    ({templates.length} milestones)
                                  </span>
                                </div>
                                <div className="flex items-center space-x-4">
                                  <div className="flex-1">
                                    <div className="flex justify-between items-center mb-1">
                                      <span className="text-xs text-gray-600">Phase Progress</span>
                                      <span className="text-xs text-gray-600">
                                        {progress.completed}/{progress.total} completed
                                      </span>
                                    </div>
                                    <div className="w-full bg-white rounded-full h-2">
                                      <div 
                                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-500"
                                        style={{ width: `${progress.percentage}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => handleAddAllPhase(phaseName)}
                                className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all btn-primary flex items-center justify-center space-x-2 shadow-md text-sm whitespace-nowrap"
                              >
                                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                                <span>Add All {templates.length}</span>
                              </button>
                            </div>
                          </div>
                          
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="overflow-hidden"
                              >
                                <div className="p-4 space-y-3">
                                  {templates.map((template, index) => {
                                    const CategoryIcon = getCategoryIcon(template.category);
                                    const isAdded = milestones.some(
                                      m => m.title === template.title && m.phase === phaseName
                                    );
                                    
                                    return (
                                      <div 
                                        key={index} 
                                        className={`p-4 border rounded-lg transition-all ${
                                          isAdded 
                                            ? 'border-green-300 bg-green-50' 
                                            : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                                        }`}
                                      >
                                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center space-x-2 mb-2">
                                              <SafeIcon icon={CategoryIcon} className="w-4 h-4 text-primary flex-shrink-0" />
                                              <h4 className="font-semibold text-gray-800 break-words">
                                                {template.title}
                                              </h4>
                                              <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${getCategoryColor(template.category)}`}>
                                                {template.category}
                                              </span>
                                            </div>
                                            <p className="text-sm text-gray-600 break-words">
                                              {template.description}
                                            </p>
                                          </div>
                                          <button
                                            onClick={() => handleAddTemplatedMilestone(template, phaseName)}
                                            disabled={isAdded}
                                            className={`px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2 whitespace-nowrap ${
                                              isAdded 
                                                ? 'bg-green-100 text-green-700 cursor-not-allowed' 
                                                : 'bg-primary text-white hover:bg-blue-700'
                                            }`}
                                          >
                                            <SafeIcon icon={isAdded ? FiCheckCircle : FiPlus} className="w-4 h-4" />
                                            <span>{isAdded ? 'Added' : 'Add'}</span>
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Add/Edit Milestone Modal */}
      <AnimatePresence>
        {showAddModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50"
              onClick={() => setShowAddModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl sm:text-2xl font-space-grotesk font-bold text-secondary">
                      {editingMilestone ? 'Edit Milestone' : 'Add Custom Milestone'}
                    </h2>
                    <button
                      onClick={() => setShowAddModal(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <SafeIcon icon={FiX} className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Milestone Title *
                      </label>
                      <input
                        type="text"
                        value={milestoneForm.title}
                        onChange={(e) =>
                          setMilestoneForm({ ...milestoneForm, title: e.target.value })
                        }
                        placeholder="e.g., Complete NCAA Registration"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <input
                        type="text"
                        value={milestoneForm.description}
                        onChange={(e) =>
                          setMilestoneForm({ ...milestoneForm, description: e.target.value })
                        }
                        placeholder="Brief description of what needs to be done"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Due Date *
                        </label>
                        <input
                          type="date"
                          value={milestoneForm.dueDate}
                          onChange={(e) =>
                            setMilestoneForm({ ...milestoneForm, dueDate: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Category (Optional)
                        </label>
                        <select
                          value={milestoneForm.category}
                          onChange={(e) =>
                            setMilestoneForm({ ...milestoneForm, category: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">Select category</option>
                          <option value="academic">Academic</option>
                          <option value="profile">Profile</option>
                          <option value="recruitment">Recruitment</option>
                          <option value="content">Content</option>
                          <option value="communication">Communication</option>
                          <option value="admin">Administrative</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notes
                      </label>
                      <textarea
                        value={milestoneForm.notes}
                        onChange={(e) =>
                          setMilestoneForm({ ...milestoneForm, notes: e.target.value })
                        }
                        placeholder="Add any additional notes or details about this milestone"
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-3 sm:space-x-4 mt-6 pt-6 border-t border-gray-200">
                    <button
                      onClick={() => setShowAddModal(false)}
                      className="w-full sm:w-auto px-6 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveMilestone}
                      className="w-full sm:w-auto bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors btn-primary flex items-center justify-center space-x-2"
                    >
                      <SafeIcon icon={FiSave} className="w-4 h-4" />
                      <span>{editingMilestone ? 'Update Milestone' : 'Add Milestone'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export default TimelineTracker;